import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search, SlidersHorizontal } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../../contexts/AuthContext";
import { getAccessToken } from "../../../../awsAuth";
import BusinessModuleCreate from "./BusinessModuleCreate";
import "./ProductsCatalogAll.scss";
import { photoVariant } from "../../../../utils/imageUrl";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

export default function ProductsCatalogAll() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId } = useAuth();

  const viewedUserId = params.userId || userId;
  const isOwn = !!(userId && String(viewedUserId) === String(userId));

  const [products, setProducts] = useState([]);
  const [businessName, setBusinessName] = useState("Каталог товарів");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [availability, setAvailability] = useState("all");

  const [editingItem, setEditingItem] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadProducts() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const isOwnFetch = userId && String(viewedUserId) === String(userId);

        const token = await getAccessToken().catch(() => null);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const productsUrl = isOwnFetch
          ? `${API_BASE}/api/business/products/me`
          : `${API_BASE}/api/business/products/user/${encodeURIComponent(
              viewedUserId
            )}`;

        const productsRes = await fetch(productsUrl, { headers });
        const productsData = await productsRes.json().catch(() => ({}));

        if (!productsRes.ok) {
          throw new Error(productsData?.error || "Не вдалося завантажити товари");
        }

        const profileRes = await fetch(
          `${API_BASE}/api/business/profile/${encodeURIComponent(
            viewedUserId
          )}`
        );

        const profileData = await profileRes.json().catch(() => ({}));

        if (!alive) return;

        setProducts(Array.isArray(productsData?.items) ? productsData.items : []);

        setBusinessName(
          profileData?.userBase?.username ||
            profileData?.profile?.title ||
            profileData?.profile?.hero?.title ||
            "Каталог товарів"
        );
      } catch (err) {
        console.error(err);
        if (alive) setError(err.message || "Помилка завантаження");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadProducts();

    return () => {
      alive = false;
    };
  }, [viewedUserId, userId]);

  // Deep-link from ListingDetails.js's "Редагувати" for a business_product
  // (?edit=<listingId>): auto-open the same edit modal the card's own
  // "Редагувати" button already uses, once the matching item has loaded.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || editingItem) return;

    const match = products.find((p) => p.listingId === editId);
    if (match) setEditingItem(match);
  }, [searchParams, products, editingItem]);

  const closeEditModal = () => {
    setEditingItem(null);

    if (searchParams.has("edit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  };

  const filteredProducts = useMemo(() => {
    let next = Array.isArray(products) ? [...products] : [];

    const q = query.trim().toLowerCase();

    if (q) {
      next = next.filter((item) => {
        const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
        return text.includes(q);
      });
    }

    if (availability !== "all") {
      next = next.filter((item) => item.availability === availability);
    }

    if (sort === "priceAsc") {
      next.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    }

    if (sort === "priceDesc") {
      next.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    }

    if (sort === "oldest") {
      next.sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));
    }

    if (sort === "newest") {
      next.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
    }

    return next;
  }, [products, query, sort, availability]);

  const formatPrice = (item) => {
    if (Number(item.price || 0) <= 0) return "Ціну уточнюйте";
    return `${item.price} ${item.currency || "ГРН"}`;
  };

  const formatAvailability = (item) => {
    if (item.availability === "preorder") return "Під замовлення";
    if (item.availability === "out_of_stock") return "Немає в наявності";
    return "В наявності";
  };

  const authHeaders = async () => {
    const token = await getAccessToken().catch(() => null);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleEditSaved = (updated) => {
    if (!updated?.listingId) return;
    setProducts((prev) =>
      prev.map((p) => (p.listingId === updated.listingId ? { ...p, ...updated } : p))
    );
  };

  const toggleHidden = async (item) => {
    if (busyId) return;

    const hiding = (item.status || "active") !== "hidden";
    const action = hiding ? "hide" : "unhide";

    setBusyId(item.listingId);
    setActionError("");

    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE}/api/business/products/${encodeURIComponent(item.listingId)}/${action}`,
        { method: "PATCH", headers }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося змінити видимість");
      }

      setProducts((prev) =>
        prev.map((p) =>
          p.listingId === item.listingId ? { ...p, status: hiding ? "hidden" : "active" } : p
        )
      );
    } catch (err) {
      setActionError(err.message || "Не вдалося змінити видимість");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    const item = confirmDeleteItem;
    if (!item) return;

    setBusyId(item.listingId);
    setActionError("");

    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE}/api/business/products/${encodeURIComponent(item.listingId)}`,
        { method: "DELETE", headers }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося видалити товар");
      }

      setProducts((prev) => prev.filter((p) => p.listingId !== item.listingId));
    } catch (err) {
      setActionError(err.message || "Не вдалося видалити товар");
    } finally {
      setBusyId(null);
      setConfirmDeleteItem(null);
    }
  };

  if (loading) {
    return (
      <main className="pcAll">
        <div className="pcAll__state">Завантаження товарів...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pcAll">
        <div className="pcAll__state pcAll__state--error">{error}</div>
      </main>
    );
  }

  return (
    <main className="pcAll">
      <header className="pcAll__hero">
        <button type="button" className="pcAll__back" onClick={() => navigate(-1)}>
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>

        <div>
          <span>Бізнес-каталог</span>
          <h1>{businessName}</h1>
          <p>{products.length} товарів у каталозі</p>
        </div>
      </header>

      <section className="pcAll__filters">
        <label className="pcAll__search">
          <Search size={18} strokeWidth={2.5} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Пошук товарів"
          />
        </label>

        <div className="pcAll__selects">
          <label>
            <SlidersHorizontal size={16} strokeWidth={2.5} />
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Нові спочатку</option>
              <option value="oldest">Старі спочатку</option>
              <option value="priceAsc">Дешевші спочатку</option>
              <option value="priceDesc">Дорожчі спочатку</option>
            </select>
          </label>

          <label>
            <select
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
            >
              <option value="all">Усі товари</option>
              <option value="available">В наявності</option>
              <option value="preorder">Під замовлення</option>
              <option value="out_of_stock">Немає в наявності</option>
            </select>
          </label>
        </div>
      </section>

      {actionError && <div className="pcAll__actionError">{actionError}</div>}

      {filteredProducts.length === 0 ? (
        <section className="pcAll__empty">
          <strong>Товарів не знайдено</strong>
          <p>Спробуй змінити пошук або фільтри.</p>
        </section>
      ) : (
        <section className="pcAll__grid">
          {filteredProducts.map((item) => {
            const photo = item.mainPhoto || item.photos?.[0] || "";
            const hidden = (item.status || "active") === "hidden";
            const isBusy = busyId === item.listingId;

            return (
              <article
                key={item.listingId}
                className={`pcAllCard${hidden ? " pcAllCard--hidden" : ""}`}
                onClick={() => navigate(`/listing/${item.listingId}`)}
              >
                <div className="pcAllCard__image">
                  {photo ? (
                    <img src={photoVariant(photo, "sm")} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span>{String(item.title || "T").slice(0, 1).toUpperCase()}</span>
                  )}
                  {hidden && <span className="pcAllCard__hiddenBadge">Приховано</span>}
                </div>

                <div className="pcAllCard__body">
                  <strong>{item.title || "Товар"}</strong>
                  <em>{formatAvailability(item)}</em>

                  <div>
                    <b>{formatPrice(item)}</b>
                    <button type="button">Відкрити</button>
                  </div>

                  {isOwn && (
                    <div
                      className="pcAllCard__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => setEditingItem(item)}
                      >
                        Редагувати
                      </button>
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => toggleHidden(item)}
                      >
                        {hidden ? "Показати" : "Приховати"}
                      </button>
                      <button
                        type="button"
                        className="pcAllCard__delete"
                        disabled={isBusy}
                        onClick={() => setConfirmDeleteItem(item)}
                      >
                        Видалити
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {isOwn && (
        <BusinessModuleCreate
          open={!!editingItem}
          moduleType="products"
          editItem={editingItem}
          onClose={closeEditModal}
          onSubmit={handleEditSaved}
        />
      )}

      {confirmDeleteItem && (
        <div className="pcAll__confirmOverlay" onMouseDown={() => setConfirmDeleteItem(null)}>
          <div className="pcAll__confirmModal" onMouseDown={(e) => e.stopPropagation()}>
            <p>
              Ви впевнені, що хочете видалити цей блок? Дані буде видалено без можливості
              відновлення.
            </p>
            <div className="pcAll__confirmActions">
              <button type="button" onClick={() => setConfirmDeleteItem(null)}>
                Скасувати
              </button>
              <button
                type="button"
                className="pcAll__confirmDelete"
                disabled={busyId === confirmDeleteItem.listingId}
                onClick={confirmDelete}
              >
                Видалити
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
